/**
 * Mobile category carousel.
 *
 * Tracks scroll direction and writes it to `data-scroll-direction` on the section
 * wrapper (.shopify-section). CSS uses that to reveal the carousel on scroll-up and
 * hide it on scroll-down, mirroring the sticky header. Desktop is unaffected because
 * the section is `display: none` ≥750px.
 */
class MobileCategoryCarousel extends HTMLElement {
  /** @type {HTMLElement} */
  #target = this;
  #lastScrollTop = 0;
  #ticking = false;
  /**
   * Minimum scroll distance (px) before the reveal/hide state flips. Acts as a
   * deadzone so finger jitter, momentum bounce, or tiny scroll-ups don't make
   * the carousel flicker up and down.
   */
  #threshold = 12;

  connectedCallback() {
    this.#target = this.closest('.shopify-section') ?? this;
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#target.dataset.scrollDirection = this.#lastScrollTop <= 0 ? 'none' : 'up';
    document.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      const delta = scrollTop - this.#lastScrollTop;

      if (scrollTop <= 0) {
        // At the very top — always show, and reset the anchor.
        this.#target.dataset.scrollDirection = 'none';
        this.#lastScrollTop = scrollTop;
      } else if (Math.abs(delta) > this.#threshold) {
        // Only flip once the user has scrolled past the deadzone. The anchor
        // (#lastScrollTop) only moves when we cross the threshold, so small
        // oscillations around it are ignored instead of toggling the state.
        this.#target.dataset.scrollDirection = delta < 0 ? 'up' : 'down';
        this.#lastScrollTop = scrollTop;
      }

      this.#ticking = false;
    });
  };
}

if (!customElements.get('mobile-category-carousel')) {
  customElements.define('mobile-category-carousel', MobileCategoryCarousel);
}
